import React, { Suspense } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import useVoyagerStore from '../stores/useVoyagerStore';
import TopBarControl from '../components/TopBarControl/TopBarControl';
import VoyagerPanes from '../components/VoyagerPanes/VoyagerPanes';
import BottomResults from '../components/BottomResults/BottomResults';
import BrowsePage from '../components/BrowsePage/BrowsePage';
import HistoryPage from '../components/HistoryPage/HistoryPage';
import WorkspaceAgentPanel from '../components/WorkspaceAgent/WorkspaceAgentPanel';
import ReviewsPage from '../components/Ladder/ReviewsPage';
import { NEW_OBJECTIVE } from '../stores/slices/navigationSlice';
import { qbWorkspace } from '../components/VoyagerPanes/queryBuilderPresentation';

const ActivityPage = React.lazy(() => import('../components/Activity/ActivityPage'));
const ElsewherePage = React.lazy(() => import('../components/Activity/ElsewherePage'));
const BrainPage = React.lazy(() => import('../components/Brain/BrainPage'));

const StudioPage = React.lazy(() => import('../components/Studio/StudioPage'));
const WorkflowsPage = React.lazy(() => import('../components/Workflows/WorkflowsPage'));
const MetricGraphPage = React.lazy(() => import('../components/MetricGraph/MetricGraphPage'));
const DashboardsPage = React.lazy(() => import('../components/Dashboards/DashboardsPage'));
const StromaConnectionsPage = React.lazy(() => import('../components/Connections/StromaConnectionsPage'));
const DefinitionsPage = React.lazy(() => import('../components/Definitions/DefinitionsPage'));
const AgentsPage = React.lazy(() => import('../components/Agents/AgentsPage'));
const AgentWall = React.lazy(() => import('../components/Agents/AgentWall'));
const ObjectivesPage = React.lazy(() => import('../components/Objectives/ObjectivesPage'));
const ObjectivePage = React.lazy(() => import('../components/Objectives/ObjectivePage'));
const NewObjective = React.lazy(() => import('../components/Objectives/NewObjective'));
const AgentStudioPage = React.lazy(() => import('../components/AgentStudio/AgentStudioPage'));
const FeedPage = React.lazy(() => import('../components/Feed/FeedPage'));
const ExtensionPreview = React.lazy(() => import('../components/Extension/ExtensionPreview'));
const SetupPage = React.lazy(() => import('../components/BuilderSetup/SetupPage'));
const BuildsPage = React.lazy(() => import('../components/Builds/BuildsPage'));
const PipelinesPage = React.lazy(() => import('../components/Pipelines/PipelinesPage'));
const PipelineBrainPage = React.lazy(() => import('../components/Pipelines/BrainPage'));

const Spinner = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

/** Objectives (#571): the list, or one objective's page. */
function Objectives() {
  const id = useVoyagerStore((s) => s.objectiveId);
  if (id === NEW_OBJECTIVE) return <NewObjective />;
  return id ? <ObjectivePage key={id} /> : <ObjectivesPage />;
}

/**
 * The view for `currentView`, hosted by the Ladder shell (#182), which
 * supplies what `landing` shows.
 */
export default function MainView({ landing }: { landing: React.ReactNode }) {
  const currentView = useVoyagerStore((s) => s.currentView);
  // the Query Builder with nothing open lands on the catalog: pick a perspective (#577)
  const catalogLanding = useVoyagerStore((s) => !s.selectedPerspective && !s.navigationPending);

  if (currentView === 'activity') return <Suspense fallback={<Spinner />}><ActivityPage /></Suspense>;
  if (currentView === 'elsewhere') return <Suspense fallback={<Spinner />}><ElsewherePage /></Suspense>;
  if (currentView === 'brain') return <Suspense fallback={<Spinner />}><BrainPage /></Suspense>;
  if (currentView === 'landing') return <>{landing}</>;
  if (currentView === 'history') return <HistoryPage />;
  if (currentView === 'reviews') return <ReviewsPage />;
  if (currentView === 'feed') return <Suspense fallback={<Spinner />}><FeedPage /></Suspense>;
  if (currentView === 'browse') return <BrowsePage />;
  if (currentView === 'metric-graph') return <Suspense fallback={<Spinner />}><MetricGraphPage /></Suspense>;
  if (currentView === 'workflows') return <Suspense fallback={<Spinner />}><WorkflowsPage /></Suspense>;
  if (currentView === 'dashboards') return <Suspense fallback={<Spinner />}><DashboardsPage /></Suspense>;
  if (currentView === 'connections') return <Suspense fallback={<Spinner />}><StromaConnectionsPage /></Suspense>;
  if (currentView === 'extension') return <Suspense fallback={<Spinner />}><ExtensionPreview /></Suspense>;
  if (currentView === 'setup') return <Suspense fallback={<Spinner />}><SetupPage /></Suspense>;
  if (currentView === 'pipelines') return <Suspense fallback={<Spinner />}><PipelinesPage /></Suspense>;
  if (currentView === 'pipeline_brain') return <Suspense fallback={<Spinner />}><PipelineBrainPage /></Suspense>;
  if (currentView === 'builds') return <Suspense fallback={<Spinner />}><BuildsPage /></Suspense>;
  if (currentView === 'definitions') return <Suspense fallback={<Spinner />}><DefinitionsPage /></Suspense>;
  if (currentView === 'studio') return <Suspense fallback={<Spinner />}><StudioPage /></Suspense>;
  if (currentView === 'agents') return <Suspense fallback={<Spinner />}><AgentsPage /></Suspense>;
  if (currentView === 'objectives') return <Suspense fallback={<Spinner />}><Objectives /></Suspense>;
  if (currentView === 'agent_wall') return <Suspense fallback={<Spinner />}><AgentWall /></Suspense>;
  if (currentView === 'agent_studio') return <Suspense fallback={<Spinner />}><AgentStudioPage /></Suspense>;

  return (
    <Box sx={qbWorkspace} data-testid="stroma-query-builder">
      <Box className="app-root" sx={{ flex: 1, minWidth: 0 }}>
        <Box className="app-topbar">
          <TopBarControl />
        </Box>
        <Box className="app-main">
          {catalogLanding ? <Box data-testid="qb-catalog" sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}><BrowsePage /></Box> : <VoyagerPanes />}
        </Box>
        <Box className="app-bottom">
          <BottomResults />
        </Box>
      </Box>
      <WorkspaceAgentPanel />
    </Box>
  );
}
